import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { LayoutGrid, Rows3, ArrowUpDown, ChevronRight, MapPin, CalendarDays, X, Search } from "lucide-react";
import { toast } from "sonner";
import { AppHeader, SourceNote } from "@/components/Layout";
import { RecallCard, StatusPill } from "@/components/RecallCard";
import { api, errMsg, tokens, fmtDate, TYPE_LABEL, STATES } from "@/lib/api";
import { useSeo } from "@/lib/seo";

const SORTS = [
  { key: "date_desc", label: "Newest first" },
  { key: "date_asc", label: "Oldest first" },
  { key: "region", label: "Region A-Z" },
  { key: "type", label: "Recall type" },
  { key: "product", label: "Product A-Z" },
];

const TYPE_OPTIONS = ["allergen", "microbial", "foreign_matter", "chemical", "labelling", "other"];

const recallYear = (r) => (r.published_at || "").slice(0, 4);

const matchesQuery = (r, q) => {
  if (!q) return true;
  const haystack = [r.product_name, r.company, r.reason, r.short_reason, r.product_size,
    r.distribution_original, ...(r.allergens || [])].filter(Boolean).join(" ").toLowerCase();
  return q.toLowerCase().split(/\s+/).filter(Boolean).every((w) => haystack.includes(w));
};

const matchesRegion = (recall, region) => {
  if (region === "all") return true;
  if (region === "NATIONAL") return !!recall.national;
  return !!recall.national || (recall.states || []).includes(region);
};

const sortRecalls = (list, key) => {
  const out = [...list];
  const byDate = (a, b) => new Date(b.published_at) - new Date(a.published_at);
  if (key === "date_desc") return out.sort(byDate);
  if (key === "date_asc") return out.sort((a, b) => -byDate(a, b));
  if (key === "region")
    return out.sort((a, b) => (a.location_label || "").localeCompare(b.location_label || "") || byDate(a, b));
  if (key === "type")
    return out.sort((a, b) => (a.recall_type || "").localeCompare(b.recall_type || "") || byDate(a, b));
  return out.sort((a, b) => (a.product_name || "").localeCompare(b.product_name || ""));
};

const ListRow = ({ recall }) => (
  <Link
    to={`/recall/${recall.id}`}
    data-testid={`recall-row-${recall.id}`}
    className="group grid grid-cols-[56px_1fr_auto] items-center gap-4 border-b border-slate-200 bg-white px-5 py-4 transition-colors last:border-b-0 hover:bg-slate-50 sm:grid-cols-[56px_2fr_1.2fr_1fr_1fr_auto]"
  >
    <div className="h-14 w-14 overflow-hidden rounded-xl bg-slate-100">
      {recall.image_url && (
        <img src={recall.image_url} alt={recall.product_name} className="h-full w-full object-cover" />
      )}
    </div>
    <div className="min-w-0">
      <p className="truncate font-medium text-slate-900" data-testid="row-product">
        {recall.product_name} {recall.product_size && `- ${recall.product_size}`}
      </p>
      <p className="mt-0.5 truncate text-sm text-red-700 sm:hidden">{recall.short_reason}</p>
      <p className="mt-0.5 flex items-center gap-3 text-xs text-slate-500 sm:hidden">
        <span>{recall.location_label}</span>
        <span>{fmtDate(recall.published_at)}</span>
      </p>
    </div>
    <p className="hidden truncate text-sm text-red-700 sm:block" data-testid="row-reason">{recall.short_reason}</p>
    <p className="hidden items-center gap-1.5 text-sm text-slate-600 sm:flex" data-testid="row-region">
      <MapPin className="h-3.5 w-3.5 shrink-0 text-slate-400" />
      <span className="truncate">{recall.location_label}</span>
    </p>
    <p className="hidden items-center gap-1.5 text-sm text-slate-600 sm:flex">
      <CalendarDays className="h-3.5 w-3.5 shrink-0 text-slate-400" /> {fmtDate(recall.published_at)}
    </p>
    <div className="flex items-center gap-2">
      <span className="hidden rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600 lg:inline">
        {TYPE_LABEL[recall.recall_type] || "Recall"}
      </span>
      <ChevronRight className="h-4 w-4 text-slate-400 transition-transform group-hover:translate-x-0.5" />
    </div>
  </Link>
);

export default function Feed() {
  useSeo({
    title: "Australian food recalls list | Current and past FSANZ recalls",
    description:
      "Browse every Australian food recall published by Food Standards Australia New Zealand. Search by product or brand and filter by state, recall type and year. Free to browse, no account needed.",
    path: "/alerts",
  });
  const [recalls, setRecalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(() => localStorage.getItem("fra_view") || "cards");
  const [sort, setSort] = useState("date_desc");
  const [region, setRegion] = useState("all");
  const [type, setType] = useState("all");
  const [year, setYear] = useState("all");
  const [perPage, setPerPage] = useState(25);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);

  useEffect(() => {
    api.get("/recalls?limit=500").then(setRecalls).catch((e) => toast.error(errMsg(e))).finally(() => setLoading(false));
  }, []);

  const setViewMode = (v) => { setView(v); localStorage.setItem("fra_view", v); };
  const years = useMemo(
    () => [...new Set(recalls.map(recallYear).filter(Boolean))].sort().reverse(),
    [recalls]
  );
  const filtered = useMemo(
    () => recalls.filter((r) =>
      matchesRegion(r, region)
      && (type === "all" || r.recall_type === type)
      && (year === "all" || recallYear(r) === year)
      && matchesQuery(r, query.trim())),
    [recalls, region, type, year, query]
  );
  const sorted = useMemo(() => sortRecalls(filtered, sort), [filtered, sort]);
  const filtersOn = region !== "all" || type !== "all" || year !== "all" || query.trim() !== "";
  const clearFilters = () => { setRegion("all"); setType("all"); setYear("all"); setQuery(""); };

  const totalPages = Math.max(1, Math.ceil(sorted.length / perPage));
  const current = Math.min(page, totalPages);
  const start = (current - 1) * perPage;
  const pageItems = sorted.slice(start, start + perPage);
  useEffect(() => { setPage(1); }, [region, type, year, sort, perPage, query]);
  const goto = (n) => {
    setPage(Math.min(Math.max(1, n), totalPages));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-white">
      <AppHeader />
      <div className="mx-auto max-w-5xl px-5 py-10 sm:py-14">
        <div className="flex flex-wrap items-end justify-between gap-5">
          <div>
            <p className="label-caps">Australian food recalls</p>
            <h1 className="mt-3 font-display text-4xl font-extrabold tracking-tighter text-slate-900 sm:text-5xl">
              Recall feed
            </h1>
            <p className="mt-2 text-sm text-slate-500" data-testid="recall-count">
              {loading
                ? "Loading..."
                : filtersOn
                  ? `${sorted.length} of ${recalls.length} recalls match your filters`
                  : `${recalls.length} recalls from Food Standards Australia New Zealand`}
            </p>
          </div>
          {!tokens.get() && (
            <Link to="/join" data-testid="feed-join-cta" className="group inline-flex items-center gap-2 rounded-full bg-red-700 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-red-800">
              Get these by SMS
              <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </Link>
          )}
        </div>

        <div className="mt-8 space-y-3 rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-[0_1px_3px_rgba(15,23,42,0.04)]">
          <div className="relative">
            <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by product, brand or reason - try peanut, Coles, listeria"
              className="w-full rounded-full border border-slate-300 bg-white py-3 pl-11 pr-11 text-sm text-slate-900 transition-colors placeholder:text-slate-400 hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900"
            />
            {query && (
              <button
                data-testid="clear-search-btn"
                onClick={() => setQuery("")}
                aria-label="Clear search"
                className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3 md:flex md:flex-wrap md:items-center md:gap-x-6 md:gap-y-3">
            <label className="flex flex-col gap-1.5 text-sm text-slate-600 md:flex-row md:items-center md:gap-2">
              <span className="label-caps">Region</span>
              <select
                data-testid="region-filter"
                value={region}
                onChange={(e) => setRegion(e.target.value)}
                className="w-full rounded-full border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-800 transition-colors hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900 md:w-auto md:py-2"
              >
                <option value="all">All regions</option>
                <option value="NATIONAL">National only</option>
                {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
            <label className="flex flex-col gap-1.5 text-sm text-slate-600 md:flex-row md:items-center md:gap-2">
              <span className="label-caps">Type</span>
              <select
                data-testid="type-filter"
                value={type}
                onChange={(e) => setType(e.target.value)}
                className="w-full rounded-full border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-800 transition-colors hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900 md:w-auto md:py-2"
              >
                <option value="all">All types</option>
                {TYPE_OPTIONS.map((t) => <option key={t} value={t}>{TYPE_LABEL[t]}</option>)}
              </select>
            </label>
            <label className="flex flex-col gap-1.5 text-sm text-slate-600 md:flex-row md:items-center md:gap-2">
              <span className="label-caps">Year</span>
              <select
                data-testid="year-filter"
                value={year}
                onChange={(e) => setYear(e.target.value)}
                className="w-full rounded-full border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-800 transition-colors hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900 md:w-auto md:py-2"
              >
                <option value="all">All years</option>
                {years.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </label>
            <label className="flex flex-col gap-1.5 text-sm text-slate-600 md:hidden">
              <span className="label-caps">Show</span>
              <select
                data-testid="per-page-select-mobile"
                value={perPage}
                onChange={(e) => setPerPage(Number(e.target.value))}
                className="w-full rounded-full border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-900"
              >
                {[25, 50, 100].map((n) => <option key={n} value={n}>{`${n} per page`}</option>)}
              </select>
            </label>
            <label className="col-span-2 flex flex-col gap-1.5 text-sm text-slate-600 md:hidden">
              <span className="label-caps flex items-center gap-1.5">
                <ArrowUpDown className="h-3.5 w-3.5 text-slate-400" /> Sort by
              </span>
              <select
                data-testid="sort-select-mobile"
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                className="w-full rounded-full border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-900"
              >
                {SORTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </label>
          </div>
          <div className="hidden flex-wrap items-center gap-x-6 gap-y-3 md:flex">
            <label className="flex items-center gap-2 text-sm text-slate-600">
              <span className="label-caps">Show</span>
              <select
                data-testid="per-page-select"
                value={perPage}
                onChange={(e) => setPerPage(Number(e.target.value))}
                className="rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-800 transition-colors hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900"
              >
                {[25, 50, 100].map((n) => <option key={n} value={n}>{`${n} per page`}</option>)}
              </select>
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-600">
              <ArrowUpDown className="h-4 w-4 text-slate-400" />
              <span className="label-caps">Sort by</span>
              <select
                data-testid="sort-select"
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                className="rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-800 transition-colors hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900"
              >
                {SORTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </label>
            <div className="ml-auto flex w-full items-center gap-1 rounded-full border border-slate-300 p-1 md:w-auto">
              <button
                data-testid="view-cards-btn"
                onClick={() => setViewMode("cards")}
                className={`inline-flex flex-1 items-center justify-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-medium transition-colors md:flex-none ${view === "cards" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}
              >
                <LayoutGrid className="h-4 w-4" /> Cards
              </button>
              <button
                data-testid="view-list-btn"
                onClick={() => setViewMode("list")}
                className={`inline-flex flex-1 items-center justify-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-medium transition-colors md:flex-none ${view === "list" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}
              >
                <Rows3 className="h-4 w-4" /> List
              </button>
            </div>
          </div>
          <div className="flex items-center gap-1 rounded-full border border-slate-300 p-1 md:hidden">
            <button
              data-testid="view-cards-btn-mobile"
              onClick={() => setViewMode("cards")}
              className={`inline-flex flex-1 items-center justify-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium transition-colors ${view === "cards" ? "bg-slate-900 text-white" : "text-slate-600"}`}
            >
              <LayoutGrid className="h-4 w-4" /> Cards
            </button>
            <button
              data-testid="view-list-btn-mobile"
              onClick={() => setViewMode("list")}
              className={`inline-flex flex-1 items-center justify-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium transition-colors ${view === "list" ? "bg-slate-900 text-white" : "text-slate-600"}`}
            >
              <Rows3 className="h-4 w-4" /> List
            </button>
          </div>
          {filtersOn && (
            <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3">
              {query.trim() && (
                <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700">
                  "{query.trim()}"
                </span>
              )}
              {region !== "all" && (
                <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700">
                  {region === "NATIONAL" ? "National only" : `Affects ${region}`}
                </span>
              )}
              {type !== "all" && (
                <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700">
                  {TYPE_LABEL[type]}
                </span>
              )}
              {year !== "all" && (
                <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700">
                  {year}
                </span>
              )}
              <button
                data-testid="clear-filters-btn"
                onClick={clearFilters}
                className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold text-slate-600 transition-colors hover:bg-slate-100"
              >
                <X className="h-3 w-3" /> Clear filters
              </button>
              {region !== "all" && region !== "NATIONAL" && (
                <span className="text-xs text-slate-500">National recalls are included because they affect {region}.</span>
              )}
            </div>
          )}
        </div>

        {loading && <p className="mt-10 text-slate-500" data-testid="feed-loading">Loading recalls...</p>}
        {!loading && recalls.length === 0 && (
          <p className="mt-10 rounded-2xl border border-slate-200 bg-white p-8 text-slate-600" data-testid="feed-empty">
            No recalls have been detected yet. New FSANZ recalls appear here automatically.
          </p>
        )}
        {!loading && recalls.length > 0 && sorted.length === 0 && (
          <div className="mt-10 rounded-2xl border border-slate-200 bg-white p-8" data-testid="no-filter-results">
            <p className="text-slate-700">No recalls match these filters.</p>            <button
              data-testid="no-results-clear-btn"
              onClick={clearFilters}
              className="mt-4 rounded-full bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-slate-800"
            >
              Clear filters
            </button>
          </div>
        )}

        {view === "cards" && sorted.length > 0 && (
          <div className="mt-8 grid gap-6 sm:grid-cols-2" data-testid="recall-list">
            {pageItems.map((r) => <RecallCard key={r.id} recall={r} />)}
          </div>
        )}

        {view === "list" && sorted.length > 0 && (
          <div className="mt-8 overflow-hidden rounded-2xl border border-slate-200" data-testid="recall-list">
            <div className="hidden grid-cols-[56px_2fr_1.2fr_1fr_1fr_auto] gap-4 border-b border-slate-200 bg-slate-50 px-5 py-3 sm:grid">
              {["", "Product", "Alert", "Region", "Published", ""].map((h, i) => (
                <span key={i} className="label-caps">{h}</span>
              ))}
            </div>
            {pageItems.map((r) => <ListRow key={r.id} recall={r} />)}
          </div>
        )}

        {sorted.length > 0 && (
          <div className="mt-8 flex flex-col items-center gap-4 sm:flex-row sm:flex-wrap sm:justify-between" data-testid="pagination">
            <p className="text-sm text-slate-500" data-testid="pagination-summary">
              Showing {start + 1}-{Math.min(start + perPage, sorted.length)} of {sorted.length}
            </p>
            {totalPages > 1 && (
              <div className="flex w-full items-center justify-between gap-2 sm:w-auto sm:justify-end">
                <button
                  data-testid="prev-page-btn"
                  onClick={() => goto(current - 1)}
                  disabled={current === 1}
                  className="rounded-full border border-slate-300 px-4 py-2.5 text-sm font-medium text-slate-700 transition-colors hover:border-slate-400 disabled:opacity-40"
                >
                  Previous
                </button>
                <span className="text-sm font-medium text-slate-700" data-testid="page-indicator">
                  Page {current} of {totalPages}
                </span>
                <button
                  data-testid="next-page-btn"
                  onClick={() => goto(current + 1)}
                  disabled={current === totalPages}
                  className="rounded-full border border-slate-300 px-4 py-2.5 text-sm font-medium text-slate-700 transition-colors hover:border-slate-400 disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}

        {view === "list" && sorted.length > 0 && (
          <div className="mt-4 flex items-center gap-2">
            <StatusPill recall={sorted[0]} />
            <span className="text-xs text-slate-500">Red = active, amber = updated, grey = historical</span>
          </div>
        )}
        <SourceNote className="mt-10" />
      </div>
    </div>
  );
}
